/**
 * service-government client. Mirrors:
 *   apps/service-government/src/modules/request/request.controller.ts
 *   apps/service-government/src/modules/stats/stats.controller.ts
 *   apps/service-government/src/modules/remittance/remittance.controller.ts
 *   apps/service-government/src/modules/lookup/lookup.controller.ts
 *   apps/service-government/src/modules/feedback/feedback.controller.ts
 *
 * RBAC: every method requires `Government/<verb>` permission.
 */
import { env } from '../env';

import { request } from './client';

const baseUrl = (): string => env.serviceGovernmentBaseUrl;

export type ServiceRequestStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'REJECTED'
  | 'REFUND_INITIATED'
  | 'REFUNDED';

export interface ServiceRequest {
  readonly id: string;
  readonly tenantId: string;
  readonly requestNumber: string;
  readonly serviceCode: string;
  readonly agency: string;
  readonly status: ServiceRequestStatus;
  readonly customerId: string | null;
  readonly outletCode: string | null;
  readonly partnerRef: string | null;
  readonly amount: string | null;
  readonly currency: string | null;
  readonly inputs: Record<string, unknown> | null;
  readonly partnerResult: Record<string, unknown> | null;
  readonly rejectedReason: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ListServiceRequestsResponse {
  readonly items: readonly ServiceRequest[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export async function listServiceRequests(query: {
  readonly status?: ServiceRequestStatus;
  readonly agency?: string;
  readonly page?: number;
  readonly pageSize?: number;
} = {}): Promise<ListServiceRequestsResponse> {
  return request<ListServiceRequestsResponse>(baseUrl(), '/v1/government/requests', {
    method: 'GET',
    query: {
      status: query.status,
      agency: query.agency,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

export async function getServiceRequest(id: string): Promise<ServiceRequest> {
  return request<ServiceRequest>(
    baseUrl(),
    `/v1/government/requests/${encodeURIComponent(id)}`,
    { method: 'GET' },
  );
}

// ─── Catalogue ────────────────────────────────────────────────────────────

export interface ServiceCatalogue {
  readonly id: string;
  readonly code: string;
  readonly displayName: string;
  readonly name?: string;
  readonly agency: string;
  readonly description: string | null;
  readonly agencyFee: string | null;
  readonly nipostFee: string | null;
  readonly currency: string | null;
  readonly active: boolean;
  readonly inputSchema: Record<string, unknown> | null;
  readonly slaHours: number | null;
}

export interface ListServicesResponse {
  readonly items: readonly ServiceCatalogue[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export async function listGovernmentServices(query: {
  readonly agency?: string;
  readonly activeOnly?: boolean;
  readonly page?: number;
  readonly pageSize?: number;
} = {}): Promise<ListServicesResponse> {
  return request<ListServicesResponse>(baseUrl(), '/v1/government/services', {
    method: 'GET',
    query: {
      agency: query.agency,
      activeOnly: query.activeOnly,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

// ─── Mutations (used by intake flow) ──────────────────────────────────────

export interface CreateServiceRequestDto {
  readonly serviceCode: string;
  readonly customerId?: string;
  readonly inputs: Record<string, unknown>;
  readonly identityRef?: string;
}

export async function createServiceRequest(
  dto: CreateServiceRequestDto,
): Promise<ServiceRequest> {
  return request<ServiceRequest>(baseUrl(), '/v1/government/requests', {
    method: 'POST',
    body: dto,
  });
}

export async function submitServiceRequest(
  id: string,
  paymentId?: string,
): Promise<ServiceRequest> {
  return request<ServiceRequest>(
    baseUrl(),
    `/v1/government/requests/${encodeURIComponent(id)}/submit`,
    { method: 'POST', body: paymentId !== undefined ? { paymentId } : {} },
  );
}

// ─── Counter-payment shortcut (intake) ────────────────────────────────────

export type CounterPayMethod = 'CASH' | 'POS' | 'TRANSFER';

export interface CounterPayDto {
  readonly method: CounterPayMethod;
  readonly reference?: string;
  readonly amount?: string;
}

export async function counterPayServiceRequest(
  id: string,
  dto: CounterPayDto,
): Promise<ServiceRequest> {
  return request<ServiceRequest>(
    baseUrl(),
    `/v1/government/requests/${encodeURIComponent(id)}/counter-pay`,
    { method: 'POST', body: dto },
  );
}

// ─── Stats / aggregators ──────────────────────────────────────────────────

export interface DashboardStats {
  readonly revenueToday: string;
  readonly revenueMonth: string;
   
  readonly requestsToday: number;
   
  readonly requestsMonth: number;
   
  readonly inFlight: number;
   
  readonly avgProcessingHours: number;
  readonly currency: string;
}

export async function getDashboardStats(): Promise<DashboardStats> {
  return request<DashboardStats>(baseUrl(), '/v1/government/stats/dashboard', {
    method: 'GET',
  });
}

export interface AgencyReport {
  readonly agency: string;
   
  readonly totalRequests: number;
   
  readonly completed: number;
   
  readonly rejected: number;
   
  readonly inProgress: number;
  readonly revenue: string;
   
  readonly avgProcessingHours: number;
   
  readonly satisfactionAverage: number | null;
   
  readonly satisfactionResponses: number;
   
  readonly outlets: readonly { operatorId: number; requests: number }[];
}

export interface ReportsBundle {
  readonly agencies: readonly AgencyReport[];
  readonly total: {
     
    readonly requests: number;
    readonly revenue: string;
  };
  readonly from: string;
  readonly to: string;
  readonly currency: string;
}

export async function getReports(query: {
  readonly agency?: string;
  readonly from?: string;
  readonly to?: string;
} = {}): Promise<ReportsBundle> {
  return request<ReportsBundle>(baseUrl(), '/v1/government/stats/reports', {
    method: 'GET',
    query: { agency: query.agency, from: query.from, to: query.to },
  });
}

export type AgencyHealthStatus = 'UP' | 'DEGRADED' | 'DOWN';

export interface AgencyHealthEntry {
  readonly agency: string;
  readonly status: AgencyHealthStatus;
   
  readonly totalRequests: number;
   
  readonly recentFailures: number;
   
  readonly successRate: number;
  readonly lastRequestAt: string | null;
}

export async function getAgencyHealth(): Promise<readonly AgencyHealthEntry[]> {
  return request<readonly AgencyHealthEntry[]>(
    baseUrl(),
    '/v1/government/stats/agency-health',
    { method: 'GET' },
  );
}

// ─── Remittance ───────────────────────────────────────────────────────────

export type RemittanceStatus = 'PENDING' | 'REMITTED' | 'OVERDUE';

export interface RemittanceRun {
  readonly id: string;
  readonly tenantId: string;
  readonly agency: string;
  readonly periodStart: string;
  readonly periodEnd: string;
   
  readonly requestCount: number;
  readonly collectedAmount: string;
  readonly nipostCharge: string;
  readonly remitAmount: string;
  readonly currency: string;
  readonly status: RemittanceStatus;
  readonly bankRef: string | null;
  readonly dueAt: string;
  readonly remittedAt: string | null;
  readonly initiatedBy: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface RemittanceSummary {
  readonly totalCollected: string;
  readonly remitted: string;
  readonly pending: string;
  readonly overdue: string;
   
  readonly pendingRuns: number;
   
  readonly overdueRuns: number;
  readonly currency: string;
}

export interface ListRemittanceRunsResponse {
  readonly items: readonly RemittanceRun[];
  // eslint-disable-next-line upmp/no-number-money -- count
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export async function getRemittanceSummary(): Promise<RemittanceSummary> {
  return request<RemittanceSummary>(baseUrl(), '/v1/government/remittance/summary', {
    method: 'GET',
  });
}

export async function listRemittanceRuns(query: {
  readonly agency?: string;
  readonly status?: RemittanceStatus;
  readonly page?: number;
  readonly pageSize?: number;
} = {}): Promise<ListRemittanceRunsResponse> {
  return request<ListRemittanceRunsResponse>(baseUrl(), '/v1/government/remittance/runs', {
    method: 'GET',
    query: {
      agency: query.agency,
      status: query.status,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

export async function createRemittanceRun(input: {
  readonly agency: string;
  readonly periodStart: string;
  readonly periodEnd: string;
}): Promise<RemittanceRun> {
  return request<RemittanceRun>(baseUrl(), '/v1/government/remittance/runs', {
    method: 'POST',
    body: input,
  });
}

export async function markRemittanceRunRemitted(
  id: string,
  input: { readonly bankRef: string; readonly remittedAt?: string },
): Promise<RemittanceRun> {
  return request<RemittanceRun>(
    baseUrl(),
    `/v1/government/remittance/runs/${encodeURIComponent(id)}/mark-remitted`,
    { method: 'POST', body: input },
  );
}

// ─── Citizen lookup ───────────────────────────────────────────────────────

export interface CitizenLookupQuery {
  readonly nin?: string;
  readonly phone?: string;
  readonly customerId?: string;
  readonly requestNumber?: string;
}

export interface CitizenLookupResponse {
  readonly items: readonly ServiceRequest[];
  // eslint-disable-next-line upmp/no-number-money -- count
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly matchedBy: string;
}

export async function lookupCitizen(
  query: CitizenLookupQuery,
): Promise<CitizenLookupResponse> {
  return request<CitizenLookupResponse>(baseUrl(), '/v1/government/lookup', {
    method: 'POST',
    body: query,
  });
}

// ─── Citizen feedback ─────────────────────────────────────────────────────

export interface SubmitFeedbackDto {
   
  readonly rating: number;
  readonly comment?: string;
}

export async function submitServiceFeedback(
  requestId: string,
  dto: SubmitFeedbackDto,
): Promise<{ id: string; rating: number; agency: string; submittedAt: string }> {
  return request(baseUrl(), `/v1/government/requests/${encodeURIComponent(requestId)}/feedback`, {
    method: 'POST',
    body: dto,
  });
}
